import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  ReplaySubject,
  Subscription,
  delay,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  take,
  tap,
} from 'rxjs';

import { ElementActions, ElementTypes, FragmentService, GaContext, ScrollService, ToasterService } from '@fcom/common';
import {
  categoryPartStatus,
  CommonBookingFeatureState,
  orderData,
  OrderPartStatus,
  serviceAvailability,
  serviceCatalogCampaigns,
  serviceCategories,
  ServicesActions,
  servicesStatus,
  upsell,
} from '@fcom/common-booking';
import { ProductCardNotification, SmpProduct } from '@fcom/common-booking/modules/ancillaries/components';
import { ButtonState } from '@fcom/common-booking/modules/ancillaries/interfaces';
import {
  categoryHasMaxPurchasesDoneForFragments,
  categoryHasNothingToSell,
  categoryHasQuotaLeft,
  combineServices,
  filterCategories,
  getCountForCategoryStream,
  getLowestPriceForCategory,
  getMaxNumberItemsForCategory,
  getPriceForCategoryStream,
  getProductTag,
  getServicesToShow,
  isPetcNotEligibleWithIncompatibleSeat,
  isSeatPurchaseClosedAfterCheckIn,
} from '@fcom/common-booking/modules/ancillaries/utils';
import {
  bookingHasDisruption,
  passengersMissingCategoryService,
  shouldShowKidsInBusinessNotification,
  shouldWeSellForCategory,
} from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { FeedbackService } from '@fcom/common/services/feedback/feedback.service';
import {
  Category,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
  FinnairServiceCatalogCampaign,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import {
  FinnairServiceItemWithUpsell,
  SelectedServiceData,
  ServicesStatus,
  UpsellCategoriesForBound,
} from '@fcom/dapi/interfaces';
import { finShare, snapshot } from '@fcom/rx';
import { ButtonMode, ButtonTheme, ButtonType } from '@fcom/ui-components';
import { LoaderTheme } from '@fcom/ui-components/components/loader/enums';
import { ModalButtons } from '@fcom/ui-components/components/modal/interfaces';
import {
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components/components/notifications/notification/notification.component';
import { EMAIL_PATTERN, isNotBlank, unique, unsubscribe } from '@fcom/core/utils';
import { ConfigService, RootPaths } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { CmsArticle } from '@fcom/core-api/interfaces';

import { MmbError } from '../../../enums';
import { ManageBookingService } from './../../../services/manage-booking.service';

@Component({
  selector: 'fin-mmb-view-route',
  templateUrl: './view-route.component.html',
  styleUrls: ['./view-route.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ManageBookingViewRouteComponent implements OnInit, OnDestroy {
  public readonly ModalButtons = ModalButtons;
  public readonly LoaderTheme = LoaderTheme;
  public readonly NotificationTheme = NotificationTheme;
  public readonly NotificationLayout = NotificationLayout;
  public readonly ButtonType = ButtonType;

  @ViewChild('travelExtrasSection', { static: false, read: ElementRef }) travelExtrasSection: ElementRef;

  @Output() focusEvent: EventEmitter<string> = new EventEmitter<string>();
  @Output() blurEvent: EventEmitter<void> = new EventEmitter<void>();

  booking$: Observable<FinnairOrder>;
  pax$: Observable<FinnairPassengerItem>;
  products$: Observable<SmpProduct[]>;
  changeFlightModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showGroupBookingEmailModal$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  groupEmailForm: UntypedFormGroup;
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  groupModalError$: Observable<MmbError>;
  isTravelExtraModal = false;
  includedServices$: Observable<FinnairServiceItem[]>;
  combinedServices$: Observable<FinnairServiceItemWithUpsell[]>;
  unconfirmedServices$: Observable<FinnairServiceItem[]>;
  bookingIsDisrupted$: Observable<boolean>;
  upsellInModal$: Observable<UpsellCategoriesForBound>;
  productsReady$: Observable<boolean>;
  showChildSafetyNotice$: Observable<boolean>;
  openAncillarySubview$: ReplaySubject<SelectedServiceData> = new ReplaySubject<SelectedServiceData>(1);
  kidsInBusinessModalOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  enableShowingSAF: boolean;
  enableKidsInBusinessNotification: boolean;
  content$: Observable<CmsArticle[]>;

  private subscriptions = new Subscription();

  constructor(
    private store$: Store<CommonBookingFeatureState>,
    private route: ActivatedRoute,
    private router: Router,
    private mmbService: ManageBookingService,
    private gtmService: GtmService,
    private toasterService: ToasterService,
    private feedbackService: FeedbackService,
    private scrollService: ScrollService,
    private element: ElementRef,
    private languageService: LanguageService,
    private configService: ConfigService,
    private fragmentService: FragmentService
  ) {}

  private getStatusStream = (
    category: Category,
    numberOfUnpaidServices$: Observable<number>,
    order: FinnairOrder
  ): Observable<ButtonState> => {
    const unpaidButtonState$ = numberOfUnpaidServices$.pipe(
      map((n) => {
        if (
          category === Category.SEAT &&
          order.services.included
            .filter((service) => service.category === Category.SEAT)
            .map((includedSeatService) =>
              includedSeatService.bounds
                .flatMap(({ segments }) => segments)
                .flatMap(({ passengers }) => passengers)
                .flatMap(({ services }) => services)
                .some(({ modifiable }) => modifiable)
            )
            .some(Boolean)
        ) {
          return ButtonState.OK;
        }
        return n > 0 ? ButtonState.OK : ButtonState.PLUS;
      })
    );
    return combineLatest([this.store$.pipe(categoryPartStatus(category)), unpaidButtonState$]).pipe(
      map(([status, unpaidButtonState]) =>
        status === OrderPartStatus.UPDATING ? ButtonState.UPDATING : unpaidButtonState
      )
    );
  };

  ngOnInit(): void {
    this.enableShowingSAF = this.configService.cfg.enableShowingSAF;
    this.enableKidsInBusinessNotification = this.configService.cfg.enableKidsInBusinessNotification;
    this.booking$ = this.store$.pipe(orderData());
    this.showChildSafetyNotice$ = this.enableKidsInBusinessNotification
      ? this.booking$.pipe(
          map((booking) =>
            booking.bounds.some((bound) => shouldShowKidsInBusinessNotification([bound], booking.passengers))
          )
        )
      : of(false);

    this.pax$ = this.booking$.pipe(map((booking) => booking.passengers[0]));
    this.feedbackService.setSurvey(QualtricsSurveys.MMB_FEEDBACK);
    this.feedbackService.setFeedbackWidgetVisibility(true);

    this.bookingIsDisrupted$ = this.booking$.pipe(
      map((booking: FinnairOrder) => bookingHasDisruption(booking)),
      finShare()
    );

    this.subscriptions.add(
      this.bookingIsDisrupted$
        .pipe(
          filter((isDisrupted) => !isDisrupted && !!this.router.parseUrl(this.router.url).fragment),
          distinctUntilChanged(),
          delay(1)
        )
        .subscribe(() => {
          const scrollElementContainer = this.element.nativeElement.querySelector(
            `.${this.router.parseUrl(this.router.url).fragment}`
          );
          if (scrollElementContainer) {
            this.scrollTo(scrollElementContainer, 100);
          }
        })
    );

    const fallbackCurrencyCode$ = this.booking$.pipe(
      map((order) => order?.prices?.included?.total?.total.totalAmount.currencyCode)
    );
    const includedServices$ = this.booking$.pipe(
      map((order) => order?.services?.included || []),
      finShare()
    );

    this.combinedServices$ = this.booking$.pipe(
      map((order) => combineServices([order?.services?.included || [], order?.services?.unpaid || []])),
      finShare()
    );

    const unpaidServices$ = this.store$.pipe(
      orderData(),
      map((order: FinnairOrder) => order?.services?.unpaid || [])
    );

    const availability$ = this.store$.pipe(serviceAvailability());

    const campaignsCategories$ = this.store$.pipe(
      serviceCatalogCampaigns(),
      startWith([] as FinnairServiceCatalogCampaign[]),
      map((campaigns) =>
        campaigns
          .flatMap((c) => c.categories)
          .flatMap((c) => c.id)
          .filter(unique)
      ),
      finShare()
    );

    this.subscriptions.add(
      this.store$
        .pipe(
          upsell(),
          filter(Boolean),
          tap(() => {
            this.store$.dispatch(ServicesActions.clearUpsell());
          })
        )
        .subscribe((data) => {
          this.openAncillarySubview$.next(data);
        })
    );

    this.products$ = combineLatest([
      combineLatest([
        this.store$.pipe(
          serviceCategories(),
          map((categories) => filterCategories(categories, this.configService.cfg.enablePriorityServices)),
          startWith(undefined)
        ),
        this.store$.pipe(servicesStatus()),
      ]).pipe(
        map(([categories, servicesStatus]) => {
          if (!categories && servicesStatus === ServicesStatus.INITIAL) {
            return [];
          }
          return categories;
        }),
        filter(Boolean)
      ),
      this.booking$.pipe(filter((booking) => booking?.eligibilities?.serviceCatalog?.isAllowedToUse)),
      availability$,
      campaignsCategories$,
      this.combinedServices$,
    ]).pipe(
      map(([categories, booking, availability, campaignsCategories, combinedServices]) =>
        categories.map((category): SmpProduct => {
          const numberOfUnpaidServices$ = getCountForCategoryStream(category.category, unpaidServices$);
          const maxNumberItems$ = getMaxNumberItemsForCategory(category.category, categories, booking);
          const purchasesDoneForFragment$ = categoryHasMaxPurchasesDoneForFragments(
            booking?.passengers.filter((passenger) => passenger.passengerTypeCode !== FinnairPassengerCode.INF).length,
            category.category,
            includedServices$,
            categories,
            booking,
            availability
          );

          const notAvailable$ = categoryHasNothingToSell(category.category, availability$, booking);
          const noQuotaLeft = !categoryHasQuotaLeft(category.category, categories);
          const maxPurchasesDone$ = purchasesDoneForFragment$.pipe(
            map((purchases) => Object.values(purchases).length > 0 && Object.values(purchases).every(Boolean))
          );
          const notEligibleWithIncompatibleSeat = isPetcNotEligibleWithIncompatibleSeat(category.category, booking);
          const lowestPrice = getLowestPriceForCategory(category);

          return {
            category: category.category,
            title: category.translations.title,
            description: category.translations.description,
            image: category.media,
            status$: this.getStatusStream(category.category, numberOfUnpaidServices$, booking),
            price$: getPriceForCategoryStream(category.category, unpaidServices$, fallbackCurrencyCode$),
            count$: numberOfUnpaidServices$,
            purchasedCount$: getCountForCategoryStream(category.category, includedServices$),
            maxNumberItems$,
            maxPurchasesDone$: maxPurchasesDone$,
            notAvailable$,
            notEligibleWithIncompatibleSeat: notEligibleWithIncompatibleSeat,
            purchasesDoneForFragment$: purchasesDoneForFragment$,
            noQuotaLeft: noQuotaLeft,
            productCardNotification$: combineLatest([maxPurchasesDone$, numberOfUnpaidServices$, notAvailable$]).pipe(
              map(([maxPurchasesDone, count, notAvailable]) =>
                this.resolveProductCardNotification(
                  category.category,
                  booking,
                  maxPurchasesDone,
                  count,
                  noQuotaLeft,
                  notEligibleWithIncompatibleSeat,
                  notAvailable
                )
              ),
              finShare()
            ),
            hideViewButton: true,
            lowestPrice: lowestPrice?.money,
            originalPrice: lowestPrice?.originalPrice,
            productTag: getProductTag(category.category, campaignsCategories, combinedServices?.[category.category], {
              showCheaperOnlineTag: false,
              enableTierBenefitTag: false,
            }),
          };
        })
      )
    );

    this.productsReady$ = this.products$.pipe(map((products) => products.length > 0));

    this.subscriptions.add(
      this.route.queryParams.pipe(take(1)).subscribe((params) => {
        this.changeFlightModalOpen$.next(params['openChangeModal'] || false);
        if (params['openChangeModal']) {
          this.router.navigateByUrl(this.router.url.substring(0, this.router.url.indexOf('?')));
        }
        if (params['seatMap']) {
          this.tryToOpenSeatMap();
          this.router.navigate([], { queryParams: { seatMap: null }, queryParamsHandling: 'merge' });
        }
      })
    );

    this.subscriptions.add(
      this.booking$
        .pipe(
          take(1),
          filter((booking) => booking.otherInformation.isGroupBooking && !booking.passengers[0].email)
        )
        .subscribe(() => {
          this.showGroupBookingEmailModal$.next(true);
        })
    );

    this.groupEmailForm = new UntypedFormGroup({
      email: new UntypedFormControl('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
    });

    this.includedServices$ = this.booking$.pipe(
      map((booking) => getServicesToShow(booking.services, 'included')),
      finShare()
    );

    this.unconfirmedServices$ = this.booking$.pipe(
      map((booking) => getServicesToShow(booking.services, 'unconfirmed')),
      finShare()
    );

    this.upsellInModal$ = this.getUpsellInModalStream();

    this.content$ = this.fragmentService.getFragment<CmsArticle>('MMB.fragments.kidsInBusinessNotice.url').pipe(
      take(1),
      map((fragment) => fragment.items as CmsArticle[])
    );
  }

  ngOnDestroy(): void {
    this.feedbackService.setFeedbackWidgetVisibility(false);
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onEmailModalClose(): void {
    this.showGroupBookingEmailModal$.next(false);
    this.groupEmailForm.get('email').setValue('');
  }

  closeEmailModal(): void {
    this.showGroupBookingEmailModal$.next(false);
  }

  openEmailModal(value: boolean): void {
    this.showGroupBookingEmailModal$.next(value);
    this.isTravelExtraModal = value;
  }

  scrollTo(templateRef: ElementRef | HTMLElement, offsetY = 60): void {
    this.scrollService.smoothScroll(templateRef, { offsetY });
  }

  openKidsInBusinessModal(): void {
    this.kidsInBusinessModalOpen$.next(true);
  }

  closeKidsInBusinessModal(): void {
    this.kidsInBusinessModalOpen$.next(false);
  }

  onSubmitEmail(): void {
    if (this.groupEmailForm.dirty && !this.groupEmailForm.disabled && this.groupEmailForm.valid) {
      this.groupEmailForm.disable();
      const { id, title, passengerTypeCode, firstName, lastName } = snapshot(this.pax$);
      const { email } = this.groupEmailForm.value;

      this.loading$.next(true);

      this.subscriptions.add(
        this.mmbService
          .updateContactDetails({
            joinFinnairPlus: false,
            passengers: [
              {
                title,
                id,
                passengerTypeCode,
                firstName,
                lastName,
                ...(isNotBlank(email) && { email }),
              },
            ],
          })
          .subscribe({
            next: () => {
              this.trackSubmit('success');
              this.loading$.next(false);
              this.closeEmailModal();
            },
            error: () => {
              this.toasterService.addMessageToQueue({
                id: 'contact-update-failed',
                contentKey: 'MMB.errors.errorContactDetails',
                theme: NotificationTheme.ALERT,
              });
              this.trackSubmit('error', 'MMB.errors.errorContactDetails');
              this.loading$.next(false);
              this.closeEmailModal();
            },
          })
      );
    } else {
      this.groupEmailForm.markAllAsTouched();
      this.groupEmailForm.updateValueAndValidity();
    }
  }

  private getUpsellInModalStream(): Observable<UpsellCategoriesForBound> {
    return this.booking$.pipe(
      map((order) => {
        return order.bounds.reduce((all, bound) => {
          all[bound.id] = {
            [Category.CABIN_BAGGAGE]:
              passengersMissingCategoryService(Category.CABIN_BAGGAGE, order, bound) &&
              shouldWeSellForCategory(Category.CABIN_BAGGAGE, order, bound),
          };
          return all;
        }, {});
      }),
      distinctUntilChanged(),
      finShare()
    );
  }

  private trackSubmit(event: string, errorKey: string = undefined) {
    this.gtmService.trackElement(
      `update-details-group-email-modal_passenger-${event}`,
      GaContext.MANAGE_BOOKING,
      ElementTypes.FORM,
      errorKey ? `ERROR::${errorKey}` : undefined,
      ElementActions.SUBMIT
    );
  }

  onUpsellClicked(data: SelectedServiceData): void {
    this.openAncillarySubview$.next(data);
  }

  onContinuePurchase(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.REVIEW.path}`
    );
  }

  tryToOpenSeatMap(): void {
    this.subscriptions.add(
      this.productsReady$.pipe(take(1)).subscribe(() => {
        this.onUpsellClicked({
          category: Category.SEAT,
          index: 0,
        });
      })
    );
  }

  private resolveProductCardNotification(
    category: Category,
    booking: FinnairOrder,
    maxPurchasesDone: boolean,
    numberOfUnpaidServices: number,
    noQuotaLeft: boolean,
    notEligibleWithIncompatibleSeat: boolean,
    notAvailable: boolean
  ): ProductCardNotification {
    const notification = { theme: NotificationTheme.INFO };
    if (maxPurchasesDone) {
      return { ...notification, messageKey: `services.${category}.maxedPurchasesNotification` };
    }
    if (noQuotaLeft && numberOfUnpaidServices === 0) {
      return {
        ...notification,
        messageKey: `services.${category}.noQuotaLeft`,
        theme: NotificationTheme.WARNING,
      };
    }
    if (notEligibleWithIncompatibleSeat) {
      return {
        ...notification,
        messageKey: `services.${category}.hasIncompatibleSeat`,
        theme: NotificationTheme.WARNING,
      };
    }
    if (category === Category.SEAT && isSeatPurchaseClosedAfterCheckIn(booking)) {
      return { ...notification, messageKey: `services.${category}.notAvailableWhenCheckInClosed` };
    }
    if (notAvailable) {
      return { ...notification, messageKey: `services.${category}.notAvailableForSell` };
    }
    return notification;
  }

  protected readonly ButtonMode = ButtonMode;
  protected readonly ButtonTheme = ButtonTheme;
}
